import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { fetchMyNotes, deleteNote } from "@/lib/api";
import { NotebookPen, Trash2, Lock, Globe, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

// Path resolver: map a note's ref_type to its live route base.
const BASE = {
  katha: "/kathas", grantha: "/granthas", mantra: "/mantras", sukta: "/suktas",
  stotra: "/stotras", puja_vidhi: "/rituals", festival: "/festivals",
  deity: "/deities", temple: "/temples", hub: "/learn",
};
const hrefFor = (n) => `${BASE[n.ref_type] || "/learn"}/${n.ref_slug}`;

export default function MyNotes() {
  const { user, ready } = useAuth();
  const { pick } = useLang();
  const [notes, setNotes] = useState(null);

  useEffect(() => { if (ready && user) fetchMyNotes().then(setNotes).catch(() => setNotes([])); }, [ready, user]);

  const grouped = useMemo(() => {
    const g = {};
    (notes || []).forEach((n) => { (g[n.ref_slug] ||= { title: n.ref_title || n.ref_slug, type: n.ref_type, slug: n.ref_slug, items: [] }).items.push(n); });
    return Object.values(g);
  }, [notes]);

  if (ready && !user) return <Navigate to="/login" replace />;

  const remove = async (id) => {
    try { await deleteNote(id); setNotes((p) => p.filter((n) => n.id !== id)); toast(pick("टिप्पणी हटाई गई", "Note deleted")); }
    catch { toast.error(pick("हटा नहीं सके", "Could not delete")); }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-20" data-testid="my-notes-page">
      <p className="overline-label text-gold">{pick("आपकी टिप्पणियाँ", "Your notes")}</p>
      <h1 className="mt-1 flex items-center gap-3 font-serif-latin text-3xl text-ink md:text-4xl">
        <NotebookPen className="h-7 w-7 text-saffron" /> {pick("मेरी टिप्पणियाँ", "My Notes")}
      </h1>
      <p className="mt-2 text-sm text-ink-3">{pick("सभी पृष्ठों पर सहेजी गई आपकी निजी टिप्पणियाँ, एक ही स्थान पर।", "All the private notes you've saved across pages, in one place.")}</p>

      {notes === null ? (
        <p className="py-20 text-center text-ink-3">…</p>
      ) : grouped.length === 0 ? (
        <div className="mt-10 border border-line bg-surface p-10 text-center" data-testid="my-notes-empty">
          <p className="text-ink-2">{pick("अभी कोई टिप्पणी नहीं।", "You haven't saved any notes yet.")}</p>
          <p className="mt-2 text-sm text-ink-3">{pick("किसी कथा, मंत्र या स्तोत्र पृष्ठ पर जाकर टिप्पणी सहेजें।", "Open any katha, mantra or stotra page and save a note.")}</p>
          <Link to="/explore" className="mt-5 inline-block bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">{pick("अन्वेषण करें", "Explore")}</Link>
        </div>
      ) : (
        <div className="mt-10 space-y-8">
          {grouped.map((grp) => (
            <div key={grp.slug} data-testid={`notes-group-${grp.slug}`}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <Link to={hrefFor(grp)} className="group inline-flex items-center gap-2 font-serif-latin text-lg text-ink hover:text-saffron">
                  {grp.title} <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                </Link>
                <span className="text-xs uppercase tracking-wide text-ink-3">{grp.items.length}</span>
              </div>
              <ul className="space-y-3">
                {grp.items.map((n) => (
                  <li key={n.id} data-testid={`my-note-${n.id}`} className="group border border-line bg-surface p-4">
                    <p className="whitespace-pre-line text-sm leading-relaxed text-ink-2">{n.body}</p>
                    <div className="mt-2 flex items-center justify-between text-xs text-ink-3">
                      <span className="inline-flex items-center gap-1">{n.visibility === "public" ? <><Globe className="h-3 w-3" /> {pick("साझा", "Shared")}</> : <><Lock className="h-3 w-3" /> {pick("निजी", "Private")}</>}</span>
                      <button onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`} className="inline-flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 hover:text-[var(--saffron-dark)]"><Trash2 className="h-3 w-3" /> {pick("हटाएँ", "Delete")}</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
